import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  MAX_IMAGE_UPLOAD_BYTES,
} from '@repo/api-contracts';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UploadItem {
  readonly key: string;
  readonly previewUrl: string;
  readonly status: 'uploading' | 'ready' | 'failed';
  readonly imageId: string | null;
  readonly file: File | null;
}

/** 추가하지 않은 파일을 사유별로 센다. */
export interface ImageRejections {
  readonly type: number;
  readonly size: number;
  readonly limit: number;
}

const isAllowedType = (type: string): type is AllowedImageContentType =>
  (ALLOWED_IMAGE_CONTENT_TYPES as readonly string[]).includes(type);

export interface ImageUploadQueueOptions {
  readonly max: number;
  readonly initial: readonly { readonly id: string; readonly url: string }[];
  readonly presign: (
    contentType: AllowedImageContentType,
  ) => Promise<{ readonly imageId: string; readonly uploadUrl: string }>;
  readonly confirm: (imageId: string) => Promise<{ readonly id: string }>;
}

/** 사진을 고르는 즉시 올린다(발급 → S3 PUT → 확인). 후기·문의가 presign·confirm 만 바꿔 같이 쓴다. 실패한 사진은 다시 시도하거나 뺄 수 있다. */
export const useImageUploadQueue = ({
  max,
  initial,
  presign,
  confirm,
}: ImageUploadQueueOptions) => {
  const [items, setItems] = useState<UploadItem[]>(() =>
    initial.map((image) => ({
      key: image.id,
      previewUrl: image.url,
      status: 'ready',
      imageId: image.id,
      file: null,
    })),
  );
  // 같은 틱에 add 가 여러 번 불려도 5장 한도를 지키도록 최신 목록을 동기로 들고 있는다.
  const latest = useRef(items);
  const [rejections, setRejections] = useState<ImageRejections | null>(null);

  // 화면을 떠나면 미리보기용 blob URL 을 풀어 준다(서버 사진은 file 이 없어 건드리지 않는다).
  useEffect(
    () => () => {
      for (const item of latest.current) {
        if (item.file !== null) URL.revokeObjectURL(item.previewUrl);
      }
    },
    [],
  );

  const commit = useCallback((next: UploadItem[]) => {
    latest.current = next;
    setItems(next);
  }, []);

  const patch = useCallback(
    (key: string, change: Partial<UploadItem>) => {
      commit(latest.current.map((item) => (item.key === key ? { ...item, ...change } : item)));
    },
    [commit],
  );

  const upload = useCallback(
    async (key: string, file: File) => {
      patch(key, { status: 'uploading' });
      try {
        const signed = await presign(file.type as AllowedImageContentType);
        const put = await fetch(signed.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!put.ok) throw new Error('upload failed');
        const view = await confirm(signed.imageId);
        patch(key, { status: 'ready', imageId: view.id });
      } catch {
        patch(key, { status: 'failed' });
      }
    },
    [confirm, patch, presign],
  );

  const add = (files: readonly File[]) => {
    const room = Math.max(max - latest.current.length, 0);
    let type = 0;
    let size = 0;
    const valid: File[] = [];
    for (const file of files) {
      if (!isAllowedType(file.type)) type += 1;
      else if (file.size > MAX_IMAGE_UPLOAD_BYTES) size += 1;
      else valid.push(file);
    }
    const accepted = valid.slice(0, room);
    const limit = valid.length - accepted.length;
    setRejections(type + size + limit > 0 ? { type, size, limit } : null);
    const added: UploadItem[] = accepted.map((file) => ({
      key: crypto.randomUUID(),
      previewUrl: URL.createObjectURL(file),
      status: 'uploading',
      imageId: null,
      file,
    }));
    if (added.length === 0) return;
    commit([...latest.current, ...added]);
    for (const item of added) {
      if (item.file !== null) void upload(item.key, item.file);
    }
  };

  const remove = (key: string) => {
    const target = latest.current.find((item) => item.key === key);
    if (target?.file) URL.revokeObjectURL(target.previewUrl);
    commit(latest.current.filter((item) => item.key !== key));
  };

  const retry = (key: string) => {
    const target = latest.current.find((item) => item.key === key);
    if (target?.file) void upload(key, target.file);
  };

  return {
    items,
    rejections,
    add,
    remove,
    retry,
    readyIds: items.flatMap((item) =>
      item.status === 'ready' && item.imageId !== null ? [item.imageId] : [],
    ),
    isBusy: items.some((item) => item.status === 'uploading'),
    hasFailed: items.some((item) => item.status === 'failed'),
  };
};
